import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErpLink } from "../components/ErpLink";
import TimeClock from "../components/TimeClock";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useApplyLeave, useEssOverview, useWithdrawLeave } from "../lib/api";
import { fmtNumber, localeFor } from "../lib/format";
import WeekHours from "./my/WeekHours";

const PILL: Record<string, string> = {
  Open: "bg-blue-50 text-brand-primary border-brand-primary/40",
  Approved: "bg-green-50 text-status-green border-status-green/40",
  Rejected: "bg-red-50 text-status-red border-status-red/40",
};

/** Employee Self-Service (WP-15.2): leave balance and history with an apply/withdraw action, an attendance
 * summary, and a payslip list — every read and write scoped to the caller's own Employee record. */
export default function EmployeeSelfService() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useEssOverview(true);
  const applyLeave = useApplyLeave();
  const withdrawLeave = useWithdrawLeave();
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");

  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "–");

  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading || !q.data) return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;
  const d = q.data;

  const submitLeave = () => {
    if (!leaveType || !fromDate || !toDate) return;
    applyLeave.mutate({ leave_type: leaveType, from_date: fromDate, to_date: toDate, description: reason || undefined }, {
      onSuccess: () => { setLeaveType(""); setFromDate(""); setToDate(""); setReason(""); },
    });
  };

  return (
    <div className="space-y-6" data-ess-page>
      <TimeClock />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="border-b border-brand-grey-light px-4 py-3"><h2 className="font-heading text-base font-semibold">{t("my.ess.leaveBalance")}</h2></div>
          {d.leave_balances.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("my.ess.noBalances")}</div> : (
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr><th className="px-4 py-2">{t("my.ess.leaveType")}</th><th className="px-4 py-2 text-right">{t("my.ess.allocated")}</th><th className="px-4 py-2 text-right">{t("my.ess.taken")}</th><th className="px-4 py-2 text-right">{t("my.ess.pending")}</th><th className="px-4 py-2 text-right">{t("my.ess.remaining")}</th></tr>
              </thead>
              <tbody>
                {d.leave_balances.map((b) => (
                  <tr key={b.leave_type} className="border-t border-brand-grey-light" data-ess-balance={b.leave_type}>
                    <td className="px-4 py-2 font-semibold">{b.leave_type}{b.is_lwp ? <span className="ml-1 text-[10px] font-normal text-brand-grey">(LWP)</span> : ""}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(b.total_leaves, lng)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtNumber(b.leaves_taken, lng)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-brand-grey">{b.leaves_pending_approval ? fmtNumber(b.leaves_pending_approval, lng) : "–"}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">{fmtNumber(b.remaining_leaves, lng)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("my.ess.applyLeave")}</h2>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
            <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="col-span-2 rounded border border-brand-grey-light px-2 py-1.5" data-ess-leave-type>
              <option value="" disabled>{t("my.ess.chooseLeaveType")}</option>
              {d.leave_balances.map((b) => <option key={b.leave_type} value={b.leave_type}>{b.leave_type} ({fmtNumber(b.remaining_leaves, lng)} {t("my.ess.remaining").toLowerCase()})</option>)}
            </select>
            <label className="text-xs text-brand-grey">{t("my.ess.fromDate")}<input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-0.5 w-full rounded border border-brand-grey-light px-2 py-1.5" /></label>
            <label className="text-xs text-brand-grey">{t("my.ess.toDate")}<input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-0.5 w-full rounded border border-brand-grey-light px-2 py-1.5" /></label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("my.ess.reason")} className="col-span-2 rounded border border-brand-grey-light px-2 py-1.5" />
          </div>
          <button disabled={!leaveType || !fromDate || !toDate || applyLeave.isPending} onClick={submitLeave}
            className="mt-2 rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" data-ess-apply-leave>
            {applyLeave.isPending ? t("my.ess.submitting") : t("my.ess.submit")}
          </button>
          {applyLeave.isError && <div className="mt-2"><ErrorBox error={applyLeave.error} /></div>}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-brand-grey-light px-4 py-3"><h2 className="font-heading text-base font-semibold">{t("my.ess.myApplications")}</h2></div>
        {d.leave_applications.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("my.ess.noApplications")}</div> : (
          <table className="w-full text-sm">
            <tbody>
              {d.leave_applications.map((a) => (
                <tr key={a.name} className="border-t border-brand-grey-light" data-ess-application={a.name}>
                  <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${PILL[a.status] ?? PILL.Open}`}>{a.status}</span></td>
                  <td className="px-4 py-2"><div className="font-semibold">{a.leave_type}</div><div className="text-[11px] text-brand-grey">{a.description || ""}</div></td>
                  <td className="px-4 py-2 text-xs text-brand-grey">{fmtDate(a.from_date)} → {fmtDate(a.to_date)}</td>
                  <td className="px-4 py-2 text-right text-xs tabular-nums">{fmtNumber(a.total_leave_days, lng)}</td>
                  <td className="px-4 py-2 text-right">
                    {a.docstatus === 0 && <button onClick={() => withdrawLeave.mutate({ name: a.name })} disabled={withdrawLeave.isPending} className="text-xs text-status-red hover:underline disabled:opacity-50" data-ess-withdraw={a.name}>
                      {withdrawLeave.isPending ? t("my.ess.withdrawing") : t("my.ess.withdraw")}
                    </button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {withdrawLeave.isError && <div className="p-3"><ErrorBox error={withdrawLeave.error} /></div>}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("my.ess.attendance")}</h2>
          <p className="text-xs text-brand-grey">{t("my.ess.attendanceHint")}</p>
          {d.attendance.total === 0 ? <p className="mt-2 text-sm text-brand-grey">{t("my.ess.noAttendance")}</p> : (
            <>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-heading text-2xl font-semibold">{d.attendance.present_rate ?? "–"}%</span>
                <span className="text-xs text-brand-grey">{t("my.ess.presentRate")}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-brand-grey">
                {Object.entries(d.attendance.by_status).map(([k, v]) => <span key={k}>{k}: {v}</span>)}
              </div>
            </>
          )}
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b border-brand-grey-light px-4 py-3"><h2 className="font-heading text-base font-semibold">{t("my.ess.payslips")}</h2></div>
          {d.payslips.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("my.ess.noPayslips")}</div> : (
            <table className="w-full text-sm">
              <tbody>
                {d.payslips.map((p) => (
                  <tr key={p.name} className="border-t border-brand-grey-light" data-ess-payslip={p.name}>
                    <td className="px-4 py-2"><ErpLink>{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</ErpLink></td>
                    <td className="px-4 py-2 text-right text-xs text-brand-grey">{t("my.ess.grossPay")} {fmtNumber(p.gross_pay, lng, 0)} {p.currency}</td>
                    <td className="px-4 py-2 text-right text-sm font-semibold tabular-nums">{fmtNumber(p.net_pay, lng, 0)} {p.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <WeekHours />
    </div>
  );
}
